import { getUserPermissionResources } from '@/api/modules/menu'
import { generateRoutesFromMenus, addDynamicRoutes, removeDynamicRoutes, extractRouteNames } from '@/utils/routeGenerator'

const state = {
  // 用户菜单数据
  userMenus: [],
  // 动态路由是否已加载
  routesLoaded: false,
  // 菜单加载状态
  menuLoading: false,
  // 菜单缓存时间戳
  menuCacheTime: null,
  // 缓存有效期（毫秒）
  cacheExpiry: 30 * 60 * 1000, // 30分钟
  // 已加载的路由名称列表
  loadedRouteNames: [],
  // 最后加载菜单的用户ID，用于检测用户切换
  lastUserId: null
}

const mutations = {
  SET_USER_MENUS(state, { menus, userId }) {
    state.userMenus = menus
    state.menuCacheTime = Date.now()
    state.lastUserId = userId
  },
  SET_ROUTES_LOADED(state, loaded) {
    state.routesLoaded = loaded
  },
  SET_LOADED_ROUTE_NAMES(state, routeNames) {
    state.loadedRouteNames = routeNames || []
  },
  SET_MENU_LOADING(state, loading) {
    state.menuLoading = loading
  },
  CLEAR_MENU_DATA(state) {
    state.userMenus = []
    state.routesLoaded = false
    state.menuCacheTime = null
    state.loadedRouteNames = []
    state.lastUserId = null
  }
}

const actions = {
  /**
   * 加载用户菜单数据
   * @param {Object} context - Vuex context
   * @param {boolean} forceRefresh - 是否强制刷新
   * @returns {Promise} 菜单数据
   */
  async loadUserMenus({ commit, state, rootState }, forceRefresh = false) {
    // 检查缓存是否有效
    if (!forceRefresh && state.userMenus.length > 0 && state.menuCacheTime) {
      const now = Date.now()
      if (now - state.menuCacheTime < state.cacheExpiry) {
        // 额外检查：如果当前用户信息发生变化，强制刷新菜单
        const currentUser = rootState.currentUser
        if (currentUser && state.lastUserId && state.lastUserId !== currentUser.id) {
          console.log('检测到用户切换，强制刷新菜单数据')
          forceRefresh = true
        } else {
          return state.userMenus
        }
      }
    }

    commit('SET_MENU_LOADING', true)
    
    try {
      // 根据环境变量控制菜单数据获取模式
      const useMockMenu = process.env.VUE_APP_USE_MOCK_MENU === 'true'
      
      if (useMockMenu) {
        console.log('使用模拟菜单数据 (VUE_APP_USE_MOCK_MENU=true)')
        const mockMenus = [
          {
            id: 1,
            menuName: 'Dashboard',
            menuTitle: '控制台',
            path: '/dashboard',
            icon: 'monitor',
            menuType: 2,
            component: 'Dashboard',
            isShow: 1,
            status: 1,
            hidden: false,
            children: []
          },
          {
            id: 2,
            menuName: 'User',
            menuTitle: '用户管理',
            path: '/user',
            icon: 'user',
            menuType: 1,
            isShow: 1,
            status: 1,
            hidden: false,
            children: [
              {
                id: 21,
                menuName: 'UserList',
                menuTitle: '用户列表',
                path: '/user/list',
                icon: 'user',
                menuType: 2,
                component: 'UserList',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              }
            ]
          },
          {
            id: 3,
            menuName: 'Order',
            menuTitle: '订单管理',
            path: '/order',
            icon: 'document',
            menuType: 1,
            isShow: 1,
            status: 1,
            hidden: false,
            children: [
              {
                id: 31,
                menuName: 'ProxyOrders',
                menuTitle: '代理订单',
                path: '/order/proxy',
                icon: 'document',
                menuType: 2,
                component: 'ProxyOrders',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              },
              {
                id: 32,
                menuName: 'RechargeOrders',
                menuTitle: '充值订单',
                path: '/order/recharge',
                icon: 'money',
                menuType: 2,
                component: 'RechargeOrderManagement',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              }
            ]
          },
          {
            id: 4,
            menuName: 'Ip',
            menuTitle: 'IP 管理',
            path: '/ip',
            icon: 'location',
            menuType: 1,
            isShow: 1,
            status: 1,
            hidden: false,
            children: [
              {
                id: 41,
                menuName: 'IpRecords',
                menuTitle: 'IP 记录',
                path: '/ip/records',
                icon: 'location',
                menuType: 2,
                component: 'IpRecords',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              },
              {
                id: 42,
                menuName: 'IpPublicPool',
                menuTitle: 'IP 池子',
                path: '/ip/public-pool',
                icon: 'connection',
                menuType: 2,
                component: 'IpPublicPool',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              }
            ]
          },
          {
            id: 5,
            menuName: 'Maintenance',
            menuTitle: '维护管理',
            path: '/maintenance',
            icon: 'setting',
            menuType: 1,
            isShow: 1,
            status: 1,
            hidden: false,
            children: [
              {
                id: 51,
                menuName: 'MenuManagement',
                menuTitle: '菜单管理',
                path: '/maintenance/menu',
                icon: 'menu',
                menuType: 2,
                component: 'MenuManagement',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              },
              {
                id: 52,
                menuName: 'RegionManagement',
                menuTitle: '地区管理',
                path: '/maintenance/region',
                icon: 'location-outline',
                menuType: 2,
                component: 'RegionManagement',
                isShow: 1,
                status: 1,
                hidden: false,
                children: []
              }
            ]
          }
        ]
        
        const currentUser = rootState.currentUser
        const userId = currentUser ? currentUser.id : null
        commit('SET_USER_MENUS', { menus: mockMenus, userId })
        return mockMenus
      }
      
      // 调用权限资源接口获取动态菜单
      console.log('调用权限资源接口获取动态菜单 (VUE_APP_USE_MOCK_MENU=false)')
      const response = await getUserPermissionResources()
      let userMenus = Array.isArray(response) ? response : []
      
      // 如果接口返回空数组，使用默认菜单兜底，避免后续生成空路由
      if (!userMenus.length) {
        console.log('权限资源接口返回空菜单，使用默认菜单兜底')
        userMenus = [
          {
            id: 1,
            menuName: 'Dashboard',
            menuTitle: '控制台',
            path: '/dashboard',
            icon: 'monitor',
            menuType: 2,
            component: 'Dashboard',
            isShow: 1,
            status: 1,
            hidden: false,
            children: []
          }
        ]
      }
      
      console.log('权限资源接口响应:', response)
      
      const currentUser = rootState.currentUser
      const userId = currentUser ? currentUser.id : null
      commit('SET_USER_MENUS', { menus: userMenus, userId })
      
      return userMenus
    } catch (error) {
      console.error('加载用户菜单失败:', error)
      
      // API调用失败时，使用默认菜单
      console.log('API调用失败，使用默认菜单')
      const defaultMenus = [
        {
          id: 1,
          menuName: 'Dashboard',
          menuTitle: '控制台',
          path: '/dashboard',
          icon: 'monitor',
          menuType: 1,
          isShow: 1,
          status: 1,
          hidden: false,
          children: []
        }
      ]
      
      const currentUser = rootState.currentUser
      const userId = currentUser ? currentUser.id : null
      commit('SET_USER_MENUS', { menus: defaultMenus, userId })
      return defaultMenus
    } finally {
      commit('SET_MENU_LOADING', false)
    }
  },

  /**
   * 生成并添加动态路由
   * @param {Object} context - Vuex context
   * @param {Object} router - Vue Router实例
   * @returns {Promise} 路由生成结果
   */
  async generateDynamicRoutes({ commit, dispatch, state }, router) {
    try {
      // 如果路由已加载，先清除
      if (state.routesLoaded && state.loadedRouteNames.length > 0) {
        removeDynamicRoutes(router, state.loadedRouteNames)
        commit('SET_ROUTES_LOADED', false)
        commit('SET_LOADED_ROUTE_NAMES', [])
      }

      // 加载用户菜单
      const userMenus = await dispatch('loadUserMenus')
      
      // 生成动态路由
      const dynamicRoutes = generateRoutesFromMenus(userMenus)
      
      // 添加到路由器
      addDynamicRoutes(router, userMenus)
      
      // 提取并保存已加载的路由名称
      const loadedRouteNames = extractRouteNames(dynamicRoutes)
      commit('SET_LOADED_ROUTE_NAMES', loadedRouteNames)
      commit('SET_ROUTES_LOADED', true)
      
      console.log('动态路由加载完成，路由数量:', loadedRouteNames.length)
      
      return dynamicRoutes
    } catch (error) {
      console.error('生成动态路由失败:', error)
      commit('SET_ROUTES_LOADED', false)
      commit('SET_LOADED_ROUTE_NAMES', [])
      throw error
    }
  },

  /**
   * 清除菜单数据和动态路由
   * @param {Object} context - Vuex context
   * @param {Object} router - Vue Router实例
   */
  clearMenuData({ commit, state }, router) {
    if (state.routesLoaded && router && state.loadedRouteNames.length > 0) {
      removeDynamicRoutes(router, state.loadedRouteNames)
    }
    commit('CLEAR_MENU_DATA')
  },

  /**
   * 刷新菜单数据
   * @param {Object} context - Vuex context
   * @param {Object} router - Vue Router实例
   * @returns {Promise} 刷新结果
   */
  async refreshMenus({ dispatch }, router) {
    // 强制刷新菜单数据
    await dispatch('loadUserMenus', true)
    
    // 重新生成动态路由
    if (router) {
      await dispatch('generateDynamicRoutes', router)
    }
  },

  /**
   * 设置测试菜单数据（仅用于开发测试）
   * @param {Object} context - Vuex context
   * @param {Array} testMenus - 测试菜单数据
   */
  setTestMenus({ commit, rootState }, testMenus) {
    const currentUser = rootState.currentUser
    const userId = currentUser ? currentUser.id : null
    commit('SET_USER_MENUS', { menus: testMenus, userId })
    commit('SET_MENU_LOADING', false)
    console.log('已设置测试菜单数据:', testMenus)
  }
}

const getters = {
  // 获取用户菜单
  userMenus: state => state.userMenus,
  
  // 获取路由加载状态
  routesLoaded: state => state.routesLoaded,
  
  // 获取菜单加载状态
  menuLoading: state => state.menuLoading,
  
  // 检查缓存是否有效
  isCacheValid: state => {
    if (!state.menuCacheTime) return false
    const now = Date.now()
    return now - state.menuCacheTime < state.cacheExpiry
  },
  
  // 获取可见菜单（用于侧边栏显示）
  visibleMenus: state => {
    const filterVisibleMenus = (menus) => {
      return menus.filter(menu => {
        // 过滤显示状态为隐藏的菜单
        if (menu.isShow === 0) return false
        
        // 递归过滤子菜单
        if (menu.children && menu.children.length > 0) {
          menu.children = filterVisibleMenus(menu.children)
        }
        
        return true
      })
    }
    
    return filterVisibleMenus([...state.userMenus])
  },
  
  // 获取扁平化的菜单权限列表
  flatMenuPermissions: state => {
    const permissions = []
    
    const extractPermissions = (menus) => {
      menus.forEach(menu => {
        if (menu.perms) {
          if (Array.isArray(menu.perms)) {
            permissions.push(...menu.perms)
          } else if (typeof menu.perms === 'string') {
            if (menu.perms.includes(',')) {
              permissions.push(...menu.perms.split(',').map(p => p.trim()))
            } else {
              permissions.push(menu.perms)
            }
          }
        }
        if (menu.children && menu.children.length > 0) {
          extractPermissions(menu.children)
        }
      })
    }
    
    extractPermissions(state.userMenus)
    return [...new Set(permissions)] // 去重
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
